<script lang="ts">
	// D8's price warning on save (UC-38), a second sheet over the item's. The
	// server answered which prices the save would write far from the ones they
	// replace; this names each one, old and new. Back returns to the item's
	// sheet with nothing saved, and Confirm saves. The owner confirms each
	// one again on the Publish sheet.

	import Sheet from '../components/Sheet.svelte';
	import { message, type Language } from '../i18n/messages';
	import { flagLine, type PriceChange, type Wording } from '../owner/draft.svelte';

	interface Props {
		language: Language;
		/** The prices the server flagged; the sheet is open while there are any. */
		flagged: PriceChange[];
		wording: Wording;
		/** The item sheet's title, which Back names. */
		backLabel: string;
		saving: boolean;
		onBack: () => void;
		onConfirm: () => void;
	}

	let { language, flagged, wording, backLabel, saving, onBack, onConfirm }: Props = $props();
</script>

<Sheet
	open={flagged.length > 0}
	level={2}
	title={message(language, 'priceCheckTitle')}
	{backLabel}
	{onBack}
	backTestId="price-check-back"
	primaryLabel={message(language, 'confirm')}
	primaryIcon="check"
	onPrimary={onConfirm}
	primaryState={saving ? 'pending' : 'idle'}
	pendingLabel={message(language, 'saving')}
	primaryTestId="price-check-confirm"
	testId="price-check"
>
	<div class="check">
		<p class="text">{message(language, 'priceCheckText')}</p>
		{#each flagged as flag (`${flag.itemId}-${flag.shopId ?? ''}`)}
			{@const line = flagLine(flag, wording)}
			<p class="line" data-testid="price-check-line">
				<span>{line.lead}</span>
				<bdi dir="ltr" class="move">{line.move}</bdi>
			</p>
		{/each}
	</div>
</Sheet>

<style>
	.check {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
		max-inline-size: var(--measure);
	}

	.text {
		margin: 0;
		font-size: var(--text-md);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.line {
		margin: 0;
		display: flex;
		flex-wrap: wrap;
		gap: 0 var(--space-2);
		padding: var(--space-3) 0;
		border-block-start: var(--border-width) solid var(--border-inset);
		font-size: var(--text-base);
		font-weight: 600;
		line-height: var(--leading-snug);
		color: var(--text-strong);
	}

	.move {
		font-family: var(--font-mono);
	}
</style>
